import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Clock, AlertTriangle, EyeOff, Layers, Tag } from "lucide-react";
import { StageStagnationConfig, StageVisibilityConfig } from "@/types/pipeline";

interface StageFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (stage: {
    name: string;
    description?: string;
    color: string;
    level: number;
    orden: number;
    stagnation_config?: StageStagnationConfig | null;
    visibility_config?: StageVisibilityConfig | null;
  }) => void;
  initialStage?: {
    name: string;
    description?: string;
    color: string;
    level: number;
    orden?: number;
    stagnation_config?: StageStagnationConfig | null;
    visibility_config?: StageVisibilityConfig | null;
  };
  isEdit?: boolean;
  defaultLevel?: number;
  defaultOrden?: number;
  maxOrden?: number;
}

const LEVEL_GROUPS = [
  { label: "Inicial", level: 0 },
  { label: "En progreso", level: 1 },
  { label: "Finalizada", level: 2 },
];

export function StageFormModal({
  isOpen,
  onClose,
  onSave,
  initialStage,
  isEdit = false,
  defaultLevel = 0,
  defaultOrden = 1,
  maxOrden = 10,
}: StageFormModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#6366f1");
  const [level, setLevel] = useState(defaultLevel);
  const [orden, setOrden] = useState(defaultOrden);
  const [stagnationDays, setStagnationDays] = useState<string>("");
  const [stagnationAction, setStagnationAction] = useState<
    "notify" | "move_to_lost"
  >("notify");
  const [visibilityValue, setVisibilityValue] = useState<string>("");
  const [visibilityUnit, setVisibilityUnit] = useState<string>("hours");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const ordenOptions = Array.from({ length: maxOrden }, (_, i) => i + 1);

  useEffect(() => {
    if (isOpen) {
      if (initialStage) {
        setName(initialStage.name);
        setDescription(initialStage.description || "");
        setColor(initialStage.color || "#6366f1");
        setLevel(initialStage.level ?? defaultLevel);
        setOrden(initialStage.orden ?? defaultOrden);
        if (initialStage.stagnation_config) {
          setStagnationDays(initialStage.stagnation_config.value.toString());
          setStagnationAction(initialStage.stagnation_config.action as any);
        } else {
          setStagnationDays("");
        }
        if (initialStage.visibility_config) {
          setVisibilityValue(initialStage.visibility_config.value.toString());
          setVisibilityUnit(initialStage.visibility_config.unit);
        } else {
          setVisibilityValue("");
        }
      } else {
        setName("");
        setDescription("");
        setColor("#6366f1");
        setLevel(defaultLevel);
        setOrden(defaultOrden);
        setStagnationDays("");
        setVisibilityValue("");
      }
      setErrors({});
    }
  }, [isOpen, initialStage, defaultLevel, defaultOrden]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = "El nombre de la etapa es obligatorio";
    }

    if (orden < 1) {
      newErrors.orden = "El orden debe ser mayor a 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    onSave({
      name: name.trim(),
      description: description.trim(),
      color,
      level,
      orden,
      stagnation_config:
        stagnationDays && level === 1
          ? {
              value: parseInt(stagnationDays),
              unit: "days",
              action: stagnationAction,
            }
          : null,
      visibility_config:
        visibilityValue && level === 2
          ? {
              value: parseInt(visibilityValue),
              unit: visibilityUnit as any,
            }
          : null,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-lg max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col">

        {/* Header */}
        <div className="bg-white border-b border-slate-100 p-4 sm:px-6 sm:py-4 z-10 relative flex-shrink-0">
          <DialogHeader>
            <div className="flex items-center space-x-3">
              <div className="bg-indigo-100/80 p-2.5 rounded-xl">
                <Layers className="h-5 w-5 text-indigo-600" />
              </div>
              <div className="text-left">
                <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
                  {isEdit ? "Editar Etapa" : "Nueva Etapa"}
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">
                  {isEdit
                    ? "Modifica los detalles de la etapa"
                    : "Define los detalles para la nueva etapa del pipeline"}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/50">
          <div className="space-y-6">

            {/* Nombre */}
            <div className="space-y-2">
              <Label htmlFor="stage-name" className="text-sm font-medium text-slate-700">
                Nombre de la etapa <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <input
                  id="stage-name"
                  placeholder="Ej: Prospecto, Cotización, Negociación..."
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.name;
                        return newErrors;
                      });
                    }
                  }}
                  className={`w-full h-10 pl-9 pr-4 rounded-lg bg-white border shadow-sm text-sm outline-none transition-all
                    ${ errors.name
                      ? "border-red-400 focus:ring-2 focus:ring-red-200"
                      : "border-slate-200 hover:border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    }`}
                  required
                />
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              </div>
              {errors.name && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <span>•</span> {errors.name}
                </p>
              )}
            </div>

            {/* Orden (solo edición) */}
            {isEdit && (
              <div className="space-y-2">
                <Label htmlFor="stage-orden" className="text-sm font-medium text-slate-700">
                  Orden de la etapa
                </Label>
                <p className="text-xs text-slate-400">
                  Posición en el pipeline (1 = primera, {maxOrden} = última)
                </p>
                <Select
                  value={orden.toString()}
                  onValueChange={(value) => setOrden(parseInt(value))}
                >
                  <SelectTrigger className="h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm">
                    <SelectValue placeholder="Selecciona un orden" />
                  </SelectTrigger>
                  <SelectContent>
                    {ordenOptions.map((option) => (
                      <SelectItem key={option} value={option.toString()}>
                        Posición {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.orden && (
                  <p className="text-red-500 text-xs">{errors.orden}</p>
                )}
              </div>
            )}

            {/* Descripción */}
            <div className="space-y-2">
              <Label htmlFor="stage-description" className="text-sm font-medium text-slate-700">
                Descripción
              </Label>
              <Textarea
                id="stage-description"
                placeholder="Ej: Etapa donde se evalúa la oportunidad inicial..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm resize-none text-sm"
              />
            </div>

            {/* Color */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-slate-700">Color de la etapa</Label>
              <div className="flex items-center gap-3">
                <div
                  className="h-10 w-10 rounded-xl border-2 border-white shadow-md flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <input
                  id="stage-color"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-10 w-10 rounded-xl cursor-pointer border-0 bg-transparent p-0 opacity-0 absolute"
                />
                <span className="text-sm font-mono text-slate-500 bg-white border border-slate-200 px-3 py-1.5 rounded-lg shadow-sm">
                  {color}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  "#6366f1", "#2563eb", "#0ea5e9", "#14b8a6",
                  "#22c55e", "#84cc16", "#eab308", "#f59e42",
                  "#f43f5e", "#ec4899", "#a21caf", "#8b5cf6",
                  "#78716c", "#64748b", "#374151",
                ].map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`h-8 w-8 rounded-xl border-2 flex-shrink-0 transition-all hover:scale-110 shadow-sm ${
                      color === c
                        ? "border-slate-800 scale-110 shadow-md"
                        : "border-transparent hover:border-slate-300"
                    }`}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                    title={c}
                  />
                ))}
              </div>
            </div>

            {/* Grupo */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-slate-700">Grupo de la etapa</Label>
              <p className="text-xs text-slate-400 -mt-1">
                El grupo determina el estado general de las oportunidades en esta etapa
              </p>
              <div className="grid grid-cols-3 gap-2">
                {LEVEL_GROUPS.map((group) => {
                  const groupStyles: Record<number, { active: string; inactive: string }> = {
                    0: { active: "bg-slate-900 text-white border-blue-600 shadow-sm shadow-blue-200", inactive: "bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50" },
                    1: { active: "bg-slate-900 text-white border-indigo-600 shadow-sm shadow-indigo-200", inactive: "bg-white border-slate-200 text-slate-600 hover:border-indigo-300 hover:bg-indigo-50" },
                    2: { active: "bg-slate-700 text-white border-slate-700 shadow-sm", inactive: "bg-white border-slate-200 text-slate-600 hover:border-slate-400 hover:bg-slate-50" },
                  };
                  const style = groupStyles[group.level];
                  return (
                    <button
                      key={group.level}
                      type="button"
                      onClick={() => setLevel(group.level)}
                      className={`rounded-xl py-2 px-3 text-sm font-medium border transition-all ${
                        level === group.level ? style.active : style.inactive
                      }`}
                    >
                      {group.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Estancamiento (solo En Progreso) */}
            {level === 1 && (
              <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="bg-amber-100 p-1.5 rounded-lg">
                    <Clock className="h-4 w-4 text-amber-600" />
                  </div>
                  <span className="text-sm font-semibold text-amber-800">Control de Estancamiento</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="stagnation-days" className="text-xs font-medium text-slate-600">Días máx. en etapa</Label>
                    <input
                      id="stagnation-days"
                      type="number"
                      min="0"
                      placeholder="Ej: 30"
                      value={stagnationDays}
                      onChange={(e) => setStagnationDays(e.target.value)}
                      className="w-full h-9 px-3 rounded-lg bg-white border border-amber-200 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all shadow-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="stagnation-action" className="text-xs font-medium text-slate-600">Acción al exceder</Label>
                    <Select value={stagnationAction} onValueChange={(v) => setStagnationAction(v as any)}>
                      <SelectTrigger className="h-9 text-sm rounded-lg border-amber-200 bg-white focus:ring-amber-100">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="notify">Solo Notificar</SelectItem>
                        <SelectItem value="move_to_lost">Mover a Perdida</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-[10px] text-amber-700 flex items-start gap-1">
                  <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  Si una oportunidad excede los días configurados, el sistema ejecutará la acción automáticamente.
                </p>
              </div>
            )}

            {/* Visibilidad (solo Finalizadas) */}
            {level === 2 && (
              <div className="rounded-xl border border-slate-200/80 bg-slate-50 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="bg-slate-200 p-1.5 rounded-lg">
                    <EyeOff className="h-4 w-4 text-slate-600" />
                  </div>
                  <span className="text-sm font-semibold text-slate-700">Control de Visibilidad</span>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="visibility-value" className="text-xs font-medium text-slate-600">Tiempo visible (en horas)</Label>
                  <input
                    id="visibility-value"
                    type="number"
                    min="0"
                    placeholder="Ej: 24"
                    value={visibilityValue}
                    onChange={(e) => setVisibilityValue(e.target.value)}
                    className="w-full h-9 px-3 rounded-lg bg-white border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all shadow-sm"
                  />
                </div>
                <p className="text-[10px] text-slate-500 flex items-start gap-1">
                  <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  Las oportunidades se ocultarán de la vista principal después de este tiempo. Dejar en blanco para mantenerlas visibles.
                </p>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50/50 border-t border-slate-100 p-5 px-6 rounded-b-[24px] flex-shrink-0 z-10">
          <div className="flex w-full justify-between items-center">
            <div className="text-xs text-slate-500 font-medium">
              <span className="text-red-500">*</span> Campos obligatorios
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="px-6 h-10 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!name.trim()}
                className="px-6 h-10 rounded-lg bg-slate-900 hover:bg-black text-white font-medium shadow-sm shadow-slate-900/20"
              >
                {isEdit ? "Guardar cambios" : "Crear Etapa"}
              </Button>
            </div>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}
